import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpBackend, HttpParams } from '@angular/common/http';
import { Observable } from 'rxjs';

import { ConfigService } from '../config/config.service';
import { LoggingService } from '../shared/logging/logging.service';
import {
  SyncStatusResult,
  SyncHistoryResult,
  SyncHistoryParams,
  TriggerFullSyncResult,
  ReconcileSyncResult,
  ResetSyncResult,
} from '../../models/image-sync.models';

/**
 * Service for interacting with the R2IT.Golf.Scoring.Images API
 * Uses HttpBackend to bypass interceptors since the Images API
 * is a separate service that does not use Keycloak auth.
 */
@Injectable({ providedIn: 'root' })
export class ImageSyncService {
  private httpClient: HttpClient;
  private configService = inject(ConfigService);
  private loggingService = inject(LoggingService);

  constructor() {
    const httpBackend = inject(HttpBackend);
    this.httpClient = new HttpClient(httpBackend);
  }

  private get basePath(): string {
    return (
      this.configService.config?.imageSyncApiBasePath ??
      'http://localhost:5100'
    );
  }

  // --- Queries ---

  /**
   * Get current sync status including health, job counts, and cursor info
   */
  getStatus(): Observable<SyncStatusResult> {
    this.loggingService.debug('ImageSync', 'Fetching sync status');
    return this.httpClient.get<SyncStatusResult>(
      `${this.basePath}/api/sync/status`
    );
  }

  /**
   * Get paginated sync history with optional filters
   */
  getHistory(params: SyncHistoryParams): Observable<SyncHistoryResult> {
    let httpParams = new HttpParams()
      .set('page', params.page.toString())
      .set('pageSize', params.pageSize.toString());

    if (params.from) {
      httpParams = httpParams.set('from', params.from);
    }
    if (params.to) {
      httpParams = httpParams.set('to', params.to);
    }
    if (params.successOnly !== null && params.successOnly !== undefined) {
      httpParams = httpParams.set('successOnly', params.successOnly.toString());
    }

    this.loggingService.debug('ImageSync', 'Fetching sync history', { params });
    return this.httpClient.get<SyncHistoryResult>(
      `${this.basePath}/api/sync/history`,
      { params: httpParams }
    );
  }

  /**
   * Check API health (database connectivity)
   */
  checkHealth(): Observable<any> {
    return this.httpClient.get(`${this.basePath}/health`);
  }

  // --- Commands ---

  /**
   * Trigger a full sync of all images from Dropbox to S3
   */
  triggerFullSync(): Observable<TriggerFullSyncResult> {
    this.loggingService.info('ImageSync', 'Triggering full sync');
    return this.httpClient.post<TriggerFullSyncResult>(
      `${this.basePath}/api/sync/trigger`,
      {}
    );
  }

  /**
   * Reconcile S3 objects against Dropbox files
   * @param dryRun If true, only preview orphaned objects without deleting
   */
  reconcile(dryRun: boolean): Observable<ReconcileSyncResult> {
    this.loggingService.info('ImageSync', 'Running reconciliation', { dryRun });
    return this.httpClient.post<ReconcileSyncResult>(
      `${this.basePath}/api/sync/reconcile`,
      {},
      {
        params: new HttpParams().set('dryRun', dryRun.toString()),
      }
    );
  }

  /**
   * Reset sync state (DEV/Local environments only)
   * @param includeCursor Whether to reset the Dropbox sync cursor
   * @param includeHistory Whether to clear sync history
   */
  resetSync(
    includeCursor: boolean,
    includeHistory: boolean
  ): Observable<ResetSyncResult> {
    this.loggingService.info('ImageSync', 'Resetting sync state', {
      includeCursor,
      includeHistory,
    });
    return this.httpClient.post<ResetSyncResult>(
      `${this.basePath}/api/sync/reset`,
      {},
      {
        params: new HttpParams()
          .set('includeCursor', includeCursor.toString())
          .set('includeHistory', includeHistory.toString()),
      }
    );
  }
}
